import { Mail, Phone, MapPin, Briefcase } from "lucide-react";
import {
  founderCompetencies,
  founderContact,
} from "@/lib/proposalData";
import { useDeployment } from "@/lib/DeploymentContext";

const FOUNDER_IMG = "/assets/george-broughton.jpg";

export default function FounderAuthority() {
  const d = useDeployment();
  return (
    <section
      id="founder"
      data-testid="section-founder"
      className="bg-[#F9F7F1] py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Heading row */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-14">
          <div className="lg:col-span-5">
            <div className="relative">
              <img
                src={FOUNDER_IMG}
                alt={d.founderTitleAlt}
                className="w-full aspect-[4/5] object-cover object-center border border-[#CBD5E1]"
              />
              <div className="absolute -bottom-6 -right-4 bg-[#8B0000] text-[#F9F7F1] p-5 max-w-[260px] hidden lg:block">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">
                  6 Years · 105-Family Caseload
                </div>
                <div className="font-display text-lg leading-tight">
                  Woodlawn Organization / WCDC · Chicago, 2001–2007
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 bg-[#8B0000]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
                06 — Led by Experience
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              George Broughton III.<br />
              <em className="italic">Program Creator &amp; Founding Architect.</em>
            </h2>

            <p className="mt-6 text-[#334155] text-base lg:text-lg leading-relaxed">
              Founder and CEO of Sterile Mate LLC, a Houston-based medical-supply
              and logistics company George built from the ground up during the
              COVID-19 pandemic to serve the Texas Medical Center. Before that —
              and the foundation of {d.fullName} — six years (April
              2001 to May 2007) as a Case Manager Service Connector at The Woodlawn
              Organization / WCDC in Chicago, managing a continuous caseload of
              <strong className="text-[#0A192F]"> 105 families</strong> through the
              full safety-net ecosystem.
            </p>

            <p className="mt-4 text-[#334155] leading-relaxed">
              Add to that fourteen years as an International Onboard Services
              Attendant at United Airlines — six of them in{" "}
              <strong className="text-[#0A192F]">Training &amp; Development</strong>{" "}
              designing FAA-compliant new-hire and recurrent curricula — and
              Bridge has a founder who has built training programs, managed real
              caseloads, and run a P&amp;L. This is not a research concept. It is
              a localized adaptation of a battle-tested operational framework.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <div className="bg-white border border-[#CBD5E1] p-5">
                <div className="font-display text-3xl text-[#0A192F]">Proven Blueprint</div>
                <p className="mt-2 text-sm text-[#475569] leading-relaxed">
                  Six years and 105 families at WCDC. The exact operational frame
                  {" "}{d.founderBuiltOn} — already executed.
                </p>
              </div>
              <div className="bg-white border border-[#CBD5E1] p-5">
                <div className="font-display text-3xl text-[#8B0000]">Founder-Operator</div>
                <p className="mt-2 text-sm text-[#475569] leading-relaxed">
                  Builds and runs companies. Sterile Mate scaled into a Texas
                  Medical Center supplier during a global supply-chain crisis —
                  the same operator&apos;s eye Bridge applies to designing the
                  {" "}{d.hubLabel}&apos;s Hub-and-Spokes architecture.
                </p>
              </div>
            </div>

            <div className="mt-6 bg-[#F9F7F1] border-l-2 border-[#8B0000] p-4 text-sm text-[#334155] leading-relaxed">
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] block mb-1">
                {d.founderRoleSection}
              </span>
              George serves as the Founding Program Architect under a sole-source
              professional-services contract — RFP authoring, data-platform
              configuration, connector curriculum design, multi-agency training,
              audit advisory. He <strong className="text-[#0A192F]">does not bid on
              any of the three Spoke RFPs</strong> and signs a 12-month
              post-contract cool-down to preserve the integrity of the
              competitive procurement.
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={`mailto:${founderContact.email}`}
                data-testid="founder-email-link"
                className="inline-flex items-center gap-2 text-[#0A192F] hover:text-[#8B0000] text-sm"
              >
                <Mail className="h-4 w-4" /> {founderContact.email}
              </a>
              <a
                href={`tel:${founderContact.phone.replace(/\./g, "")}`}
                data-testid="founder-phone-link"
                className="inline-flex items-center gap-2 text-[#0A192F] hover:text-[#8B0000] text-sm"
              >
                <Phone className="h-4 w-4" /> {founderContact.phone}
              </a>
              <span className="inline-flex items-center gap-2 text-[#475569] text-sm">
                <MapPin className="h-4 w-4" /> {founderContact.city}
              </span>
            </div>
          </div>
        </div>

        {/* Pull-quote */}
        <blockquote className="border-l-2 border-[#8B0000] pl-6 italic font-display text-xl lg:text-2xl text-[#0A192F] leading-snug max-w-4xl mb-16">
          &ldquo;{d.founderQuoteCity}&rdquo;
          <footer className="not-italic font-mono text-[10px] uppercase tracking-widest text-[#475569] mt-4">
            — George Broughton III, Program Creator
          </footer>
        </blockquote>

        {/* Core competencies */}
        <div className="bg-white border border-[#CBD5E1] p-7 lg:p-10">
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4">
              <Briefcase className="h-8 w-8 text-[#8B0000] mb-3" />
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-1">
                Core Competencies
              </div>
              <div className="font-display text-2xl text-[#0A192F] leading-tight">
                What George brings to {d.cityRegion} on day one.
              </div>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {founderCompetencies.map((c, i) => (
                <div
                  key={c}
                  data-testid={`founder-competency-${i}`}
                  className="flex items-start gap-3 text-[#0A192F]"
                >
                  <span className="font-mono text-[10px] text-[#8B0000] mt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-sm leading-snug">{c}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
